import { z } from 'zod'

import { auditShape } from '@/shared/details'
import { recordPath, recordRoute } from '@/shared/registry'

/** Where the tag and tag group pages live. */
export const tagsPath = '/catalog/tags'
export const createTagPath = `${tagsPath}/new`
export const tagRoute = recordRoute('catalog', 'tags')
export const editTagRoute = `${tagRoute}/edit`
export const tagGroupsPath = '/catalog/tag-groups'
export const createTagGroupPath = `${tagGroupsPath}/new`
export const tagGroupRoute = recordRoute('catalog', 'tag-groups')
export const editTagGroupRoute = `${tagGroupRoute}/edit`

/** An organization's catalog settings page: the tag groups its courses show. */
export const organizationCatalogRoute = '/catalog/organizations/:publicId/settings'

export function tagPath(id: string): string {
  return recordPath('catalog', 'tags', id)
}

export function tagGroupPath(id: string): string {
  return recordPath('catalog', 'tag-groups', id)
}

export function organizationCatalogPath(organizationId: string): string {
  return `/catalog/organizations/${encodeURIComponent(organizationId)}/settings`
}

/** The bases of Tag and Tag Group Details' slots (`detailsSlots`). */
export const tagDetailsSlot = 'catalog.tag-details'
export const tagGroupDetailsSlot = 'catalog.tag-group-details'

/** The API's tag endpoints. */
export const tagsApi = '/catalog/v1/tags'
export const tagGroupsApi = '/catalog/v1/tag-groups'

export function tagApi(id: string): string {
  return `${tagsApi}/${encodeURIComponent(id)}`
}

export function tagGroupApi(id: string): string {
  return `${tagGroupsApi}/${encodeURIComponent(id)}`
}

export function organizationTagGroupsApi(organizationId: string): string {
  return `/catalog/v1/organizations/${encodeURIComponent(organizationId)}/tag-groups`
}

/** The tags an organization may use, for a chooser. */
export function organizationAvailableTagsApi(organizationId: string): string {
  return `/catalog/v1/organizations/${encodeURIComponent(organizationId)}/tags/available`
}

/** The tag groups an organization may show, for a chooser. */
export function organizationAvailableTagGroupsApi(organizationId: string): string {
  return `${organizationTagGroupsApi(organizationId)}/available`
}

export function courseTagsApi(courseId: string): string {
  return `/catalog/v1/courses/${encodeURIComponent(courseId)}/tags`
}

/** What each tag action needs. */
export const readTagsPermission = 'catalog.tags.read'
export const writeTagsPermission = 'catalog.tags.write'
export const deleteTagsPermission = 'catalog.tags.delete'

const nameSchema = z.object({ id: z.string(), name: z.string(), isEnabled: z.boolean() })

/** A tag another record holds, by name. */
export type TagName = z.infer<typeof nameSchema>

const tagSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    isEnabled: z.boolean(),
    organizationId: z.string(),
    organizationName: z.string(),
    licenseAgreementId: z.string().nullish(),
    licenseAgreementName: z.string().nullish(),
    isSharedWithEveryOrganization: z.boolean(),
    dependsOn: z.array(nameSchema),
  })
  .extend(auditShape)

/** A tag as its pages show it. */
export type Tag = z.infer<typeof tagSchema>

export function parseTag(body: unknown): Tag {
  return tagSchema.parse(body)
}

const groupSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    isEnabled: z.boolean(),
    isExpanded: z.boolean(),
    order: z.number().int(),
    isMutuallyExclusive: z.boolean(),
    isAlphabetical: z.boolean(),
    organizationId: z.string(),
    organizationName: z.string(),
    tags: z.array(nameSchema),
  })
  .extend(auditShape)

/** A tag group as its pages show it, with its tags. */
export type TagGroup = z.infer<typeof groupSchema>

export function parseTagGroup(body: unknown): TagGroup {
  return groupSchema.parse(body)
}

const organizationGroupsSchema = z.object({
  organizationId: z.string(),
  inheritedFromId: z.string().nullish(),
  inheritedFromName: z.string().nullish(),
  groups: z.array(groupSchema),
})

/** The tag groups an organization's courses show, its own or an ancestor's. */
export type OrganizationTagGroups = z.infer<typeof organizationGroupsSchema>

export function parseOrganizationTagGroups(body: unknown): OrganizationTagGroups {
  return organizationGroupsSchema.parse(body)
}

const availableSchema = z.object({ id: z.string(), name: z.string(), organizationName: z.string() })

/** A tag a chooser offers. */
export type AvailableTag = z.infer<typeof availableSchema>

export function parseAvailableTag(item: unknown): AvailableTag {
  return availableSchema.parse(item)
}

const courseTagSchema = z.object({ id: z.string(), name: z.string(), isEnabled: z.boolean(), dependsOnTagIds: z.array(z.string()) })

/** A tag in a course's Tags section, with the tags one of which it needs first. */
export type CourseTag = z.infer<typeof courseTagSchema>

const courseTagsSchema = z.object({
  courseId: z.string(),
  groups: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      isExpanded: z.boolean(),
      isMutuallyExclusive: z.boolean(),
      tags: z.array(courseTagSchema),
    }),
  ),
  tags: z.array(courseTagSchema),
  needed: z.array(nameSchema),
})

/** A course's Tags section. */
export type CourseTags = z.infer<typeof courseTagsSchema>

export function parseCourseTags(body: unknown): CourseTags {
  return courseTagsSchema.parse(body)
}
